import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import { Spinner, ToastContainer, DialogoContainer } from './components/UI';
import './index.css';

// Code splitting: cada módulo se descarga on-demand al navegar a su ruta, en vez
// de viajar todo en el bundle inicial. Mantiene fuera del critical path las
// páginas pesadas/poco frecuentes (inventario, devoluciones y sobre todo
// facturación, que arrastra recharts ~400KB). Login y Dashboard quedan eager
// por ser los puntos de entrada más usados.
const CotizacionesList = lazy(() => import('./pages/cotizaciones').then(m => ({ default: m.CotizacionesList })));
const NuevaCotizacion  = lazy(() => import('./pages/cotizaciones').then(m => ({ default: m.NuevaCotizacion })));
const CotizacionDetalle = lazy(() => import('./pages/cotizaciones/CotizacionDetalle'));
const AdminPanel = lazy(() => import('./pages/admin'));
const Profile = lazy(() => import('./pages/profile'));
const Documentos = lazy(() => import('./pages/documentos'));
const GuiaLlamada = lazy(() => import('./pages/guia-llamada'));
const CarteraPage = lazy(() => import('./pages/cartera/CarteraPage'));
const FacturacionPage = lazy(() => import('./pages/facturacion/FacturacionPage'));
const PronosticoPage = lazy(() => import('./pages/cartera/PronosticoPage'));
const ProyeccionPage = lazy(() => import('./pages/cartera/proyeccion/ProyeccionPage'));
const SeguimientoPage = lazy(() => import('./pages/cartera/SeguimientoPage'));
const ClientesPage = lazy(() => import('./pages/cartera/ClientesPage'));
const DesarrolloCarteraPage = lazy(() => import('./pages/cartera/DesarrolloCarteraPage'));
const DesarrollosPage = lazy(() => import('./pages/desarrollos'));
const NuevoDesarrolloPage = lazy(() => import('./pages/desarrollos/NuevoDesarrollo'));
const NuevoEspontaneoPage = lazy(() => import('./pages/desarrollos/NuevoEspontaneo'));
const DesarrollosAgendaPage = lazy(() => import('./pages/desarrollos/AgendaPage'));
const DesarrollosDetalle = lazy(() => import('./pages/desarrollos/DesarrollosDetalle'));
const DevolucionesList = lazy(() => import('./pages/devoluciones/DevolucionesList'));
const DevolucionNueva = lazy(() => import('./pages/devoluciones/DevolucionNueva'));
const DevolucionDetalle = lazy(() => import('./pages/devoluciones/DevolucionDetalle'));
const InventarioHome = lazy(() => import('./pages/inventario/InventarioHome'));
const ImportacionPage = lazy(() => import('./pages/inventario/ImportacionPage'));
const MaestrosPage = lazy(() => import('./pages/inventario/MaestrosPage'));
const SesionesPageInv = lazy(() => import('./pages/inventario/SesionesPage'));
const SesionDetalle = lazy(() => import('./pages/inventario/SesionDetalle'));
const ConteoPage = lazy(() => import('./pages/inventario/ConteoPage'));
const ConciliacionPage = lazy(() => import('./pages/inventario/ConciliacionPage'));
const BalancesPage = lazy(() => import('./pages/inventario/BalancesPage'));
const BalanceDetalle = lazy(() => import('./pages/inventario/BalanceDetalle'));
const CalculadoraCuotas = lazy(() => import('./pages/cuotas/CalculadoraCuotasPage'));
const ArcadeHub = lazy(() => import('./pages/arcade/ArcadeHub'));
const FirmasPage = lazy(() => import('./pages/firmas/FirmasPage'));
const AutomatizacionesPage = lazy(() => import('./pages/automatizaciones/AutomatizacionesPage'));

function Guard({ children, adminOnly = false }) {
  const { user, loading, effectiveRole, modules } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}><Spinner /></div>;
  if (!user)   return <Navigate to="/login" replace />;
  const matchedModule = modules.find(m => pathname.startsWith(m.path));
  // dev es llave maestra: nunca se bloquea (coherente con el backend).
  if (effectiveRole !== 'dev') {
    if (matchedModule && !matchedModule.active) return <Navigate to="/" replace />;
    if (matchedModule && !(matchedModule.roles || []).includes(effectiveRole)) return <Navigate to="/" replace />;
    if (adminOnly && !['admin', 'dev'].includes(effectiveRole)) return <Navigate to="/" replace />;
  }
  return children;
}

function Shell() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  return (
    <div className="app-shell">
      {user && <Navbar onToggleSidebar={() => setSidebarOpen(o => !o)} />}
      <div className="app-body">
        {user && <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />}
        <div className="app-content">
          <ErrorBoundary>
          <Suspense fallback={<div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}><Spinner /></div>}>
          <Routes>
            <Route path="/login"   element={<Login />} />
            <Route path="/"        element={<Guard><Dashboard /></Guard>} />
            <Route path="/perfil"  element={<Guard><Profile /></Guard>} />

            {/* ── Módulo: Cotizaciones ── */}
            <Route path="/cotizaciones"       element={<Guard><CotizacionesList /></Guard>} />
            <Route path="/cotizaciones/nueva" element={<Guard><NuevaCotizacion /></Guard>} />
            <Route path="/cotizaciones/:id"   element={<Guard><CotizacionDetalle /></Guard>} />

            {/* ── Módulo: Documentos ── */}
            <Route path="/documentos" element={<Guard><Documentos /></Guard>} />

            {/* ── Módulo: Guía de llamada ── */}
            <Route path="/guia-llamada" element={<Guard><GuiaLlamada /></Guard>} />

            {/* ── Módulo: Mi Cartera ── */}
            <Route path="/cartera"              element={<Guard><CarteraPage /></Guard>} />
            <Route path="/cartera/pronostico"   element={<Guard><PronosticoPage /></Guard>} />
            <Route path="/cartera/seguimiento"  element={<Guard><SeguimientoPage /></Guard>} />
            <Route path="/cartera/clientes"    element={<Guard><ClientesPage /></Guard>} />
            <Route path="/cartera/desarrollo"  element={<Guard><DesarrolloCarteraPage /></Guard>} />
            <Route path="/cartera/proyeccion"  element={<Guard><ProyeccionPage /></Guard>} />

            {/* ── Módulo: Facturación ── */}
            <Route path="/facturacion" element={<Guard><FacturacionPage /></Guard>} />

            {/* ── Módulo: Devoluciones ── */}
            <Route path="/devoluciones"       element={<Guard><DevolucionesList /></Guard>} />
            <Route path="/devoluciones/nueva" element={<Guard><DevolucionNueva /></Guard>} />
            <Route path="/devoluciones/:id"   element={<Guard><DevolucionDetalle /></Guard>} />

            {/* ── Módulo: Desarrollos ── */}
            <Route path="/desarrollos"       element={<Guard adminOnly><DesarrollosPage /></Guard>} />
            <Route path="/desarrollos/nuevo" element={<Guard adminOnly><NuevoDesarrolloPage /></Guard>} />
            {/* El gate real es @require_dev en el backend; la página además se
                autoprotege por si alguien entra por URL. */}
            <Route path="/desarrollos/espontaneo" element={<Guard adminOnly><NuevoEspontaneoPage /></Guard>} />
            <Route path="/desarrollos/agenda"     element={<Guard adminOnly><DesarrollosAgendaPage /></Guard>} />
            <Route path="/desarrollos/:id"   element={<Guard adminOnly><DesarrollosDetalle /></Guard>} />

            {/* ── Módulo: Inventario ── */}
            <Route path="/inventario"                        element={<Guard><InventarioHome /></Guard>} />
            <Route path="/inventario/importacion"            element={<Guard><ImportacionPage /></Guard>} />
            <Route path="/inventario/maestros"               element={<Guard><MaestrosPage /></Guard>} />
            <Route path="/inventario/sesiones"               element={<Guard><SesionesPageInv /></Guard>} />
            <Route path="/inventario/sesiones/:id"           element={<Guard><SesionDetalle /></Guard>} />
            <Route path="/inventario/sesiones/:id/conteo"    element={<Guard><ConteoPage /></Guard>} />
            <Route path="/inventario/sesiones/:id/conciliacion" element={<Guard><ConciliacionPage /></Guard>} />
            <Route path="/inventario/balances"               element={<Guard><BalancesPage /></Guard>} />
            <Route path="/inventario/balances/:id"           element={<Guard><BalanceDetalle /></Guard>} />

            {/* ── Módulo: Calculadora de cuotas ── */}
            <Route path="/calculadora-cuotas" element={<Guard><CalculadoraCuotas /></Guard>} />

            {/* ── Módulo: Arcade ── */}
            {/* Solo el hub es una página React. Los juegos son HTML servido por
                el backend bajo /arcade, así que no tienen ruta acá. */}
            <Route path="/arcade" element={<Guard><ArcadeHub /></Guard>} />

            {/* ── Módulo: Firmas de Gmail (admin / dev) ── */}
            <Route path="/firmas" element={<Guard adminOnly><FirmasPage /></Guard>} />

            {/* ── Módulo: Automatizaciones (monitor, admin / dev) ── */}
            <Route path="/automatizaciones" element={<Guard adminOnly><AutomatizacionesPage /></Guard>} />

            {/* ── Panel Admin ── */}
            <Route path="/admin" element={<Guard adminOnly><AdminPanel /></Guard>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
          <ToastContainer />
          <DialogoContainer />
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
